// ==================== 聊天文本中的网址识别 ====================
// 用于聊天页：把消息文本中的网址渲染成可点击链接
// 支持三类写法：
//   1. 带协议的完整地址：https://a.com/x、http://a.com
//   2. www 开头的裸域名：www.a.com/x
//   3. 常见顶级域名的裸域名：a.com/x、item.shop.cn:8080/y

// 常见顶级域名（裸域名必须命中其中之一，避免把「3.5折」「1.2」这类数字误判成网址）
// 注意：多段后缀（com.cn / co.uk）必须放在单段后缀之前，否则 a.com.cn 只会匹配到 a.com
const TLDS = 'com\\.cn|net\\.cn|org\\.cn|com\\.hk|co\\.uk|com|cn|net|org|top|xyz|info|biz|vip|shop|store|site|online|club|me|cc|tv|io|ai|dev|fun|live|link|tech|work|group|ltd|wang|xin|icu|edu|gov'

// 网址中不允许出现的字符（空白 + 中英文标点，避免把后面的句子一起吃进链接）
const URL_CHAR = '[^\\s，。；：！？、"\'<>（）【】《》]'

// 网址主体：协议地址 / www 裸域名 / 命中常见顶级域名的裸域名
const URL_RE = new RegExp(
  '(https?:\\/\\/' + URL_CHAR + '+)|' +
  '(www\\.[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)+(?::\\d+)?(?:\\/' + URL_CHAR + '*)?)|' +
  '([a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*\\.(?:' + TLDS + ')(?::\\d+)?(?:\\/' + URL_CHAR + '*)?)',
  'gi'
)

// 命中位置的前一个字符若是字母 / 数字 / @ . _ - /，说明只是更长片段的一部分
// （例如邮箱里的 mail.com、路径中的片段名），不作为独立链接
const PREV_BLOCK_RE = /[a-zA-Z0-9@._/-]/

// 网址结尾常见的多余标点（中文句子里的「。」「，」和括号内的结尾括号等）
const URL_TAIL_RE = /[.,;:!?)\]}>'"”’”，。；：！？）】》]+$/

// 去掉网址结尾多余标点，返回干净的网址
export const trimUrlTail = (url) => String(url || '').replace(URL_TAIL_RE, '')

// 补全协议：www.a.com → http://www.a.com
export const normalizeUrl = (raw) => {
  const url = String(raw || '').trim()
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  if (/^\/\//.test(url)) return 'http:' + url
  return 'http://' + url
}

// 是否是可打开的外链（http / https）
export const isHttpUrl = (url) => /^https?:\/\//i.test(String(url || '').trim())

// 是否是店内的「http/https 外链」（用于判断能否放进内置浏览器）
export const isExternalHttpUrl = (raw) => isHttpUrl(normalizeUrl(raw))

// 把文本切分为 [{ type: 'text' | 'link', text, href }]
export const splitTextLinks = (text) => {
  const str = String(text === null || text === undefined ? '' : text)
  if (!str) return []
  const segments = []
  let last = 0
  let match
  URL_RE.lastIndex = 0
  while ((match = URL_RE.exec(str)) !== null) {
    const url = trimUrlTail(match[0])
    if (!url) continue
    const start = match.index
    // 命中的只是更长片段的一部分（如邮箱 test@mail.com 里的 mail.com）时跳过
    if (start > 0 && PREV_BLOCK_RE.test(str.charAt(start - 1))) continue
    const end = start + url.length
    if (start > last) segments.push({ type: 'text', text: str.slice(last, start) })
    segments.push({ type: 'link', text: url, href: normalizeUrl(url) })
    last = end
  }
  if (!last) return [{ type: 'text', text: str }]
  if (last < str.length) segments.push({ type: 'text', text: str.slice(last) })
  return segments
}

// 文本中识别到的全部网址（已去重）
export const findLinks = (text) => {
  const links = []
  splitTextLinks(text).forEach(seg => {
    if (seg.type !== 'link') return
    if (links.indexOf(seg.href) === -1) links.push(seg.href)
  })
  return links
}

// 是否是店内的链接（站内路径 /prodetail/12 或指向本站域名的完整地址）
// 站内链接直接走前端路由跳转，无需进入内置浏览器
export const isInternalUrl = (raw) => {
  const url = String(raw || '').trim()
  if (!url) return false
  // 站内相对路径（不含 // 开头的协议相对地址）
  if (url.charAt(0) === '/' && url.charAt(1) !== '/') return true
  try {
    const a = document.createElement('a')
    a.href = normalizeUrl(url)
    return a.host === window.location.host && /^https?:$/i.test(a.protocol)
  } catch (e) {
    return false
  }
}

// 站内链接 → 前端路由路径（/prodetail/12?x=1#y）
export const toInternalPath = (raw) => {
  const url = String(raw || '').trim()
  if (!url) return ''
  if (url.charAt(0) === '/') return url
  try {
    const a = document.createElement('a')
    a.href = normalizeUrl(url)
    return a.pathname + a.search + a.hash
  } catch (e) {
    return ''
  }
}

// 取网址的域名（内置浏览器标题 / 底部地址展示用）
export const getUrlHost = (raw) => {
  const url = normalizeUrl(raw)
  if (!url) return ''
  try {
    const a = document.createElement('a')
    a.href = url
    return a.host || ''
  } catch (e) {
    return ''
  }
}
